import namer from 'color-namer';

/**
 * Gets the common name for a given HEX color code.
 * Uses the 'roygbiv' set from color-namer for basic, common names.
 * 
 * @param hexCode The HEX color code (e.g., "#FF0000" or "FF0000").
 * @returns The common name of the color (e.g., "red") or null if no suitable name is found.
 */
export function getSimpleColorName(hexCode: string): string | null {
  try {
    // Ensure hexCode has a # prefix for color-namer
    const prefixedHex = hexCode.startsWith('#') ? hexCode : `#${hexCode}`;
    
    // Get names from the 'roygbiv' set (basic colors) and 'basic' set as fallback
    // color-namer returns an object like: { roygbiv: [{ name: 'red', hex: '#ff0000', distance: 0 }, ...], ... }
    const names = namer(prefixedHex, { pick: ['roygbiv', 'basic'] });

    // Prefer roygbiv if available and a good match (low distance)
    if (names.roygbiv && names.roygbiv.length > 0) {
      // Typically, the first result is the closest. 
      // We might add a distance threshold if needed, but for simple names, it's often good enough.
      const bestMatch = names.roygbiv[0];
      // Simple check: if distance is 0 it's an exact match from the list.
      // For closest names, we accept a small distance.
      if (bestMatch && bestMatch.distance < 50) { // Arbitrary distance threshold
        return bestMatch.name;
      }
    }
    
    // Fallback to basic names if roygbiv didn't yield a good result
    if (names.basic && names.basic.length > 0) {
      const bestMatch = names.basic[0];
      if (bestMatch && bestMatch.distance < 70) { // Slightly higher threshold for basic names
         return bestMatch.name;
      }
    }

    return null; // No suitable name found
  } catch (error) {
    console.error('Error getting color name:', error);
    return null;
  }
} 